import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utcPlugin from 'dayjs/plugin/utc';

// Our API returns dates either in timestamps or in UTC

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

// the timezone on the user's machine
const guessedTimezone = dayjs.tz.guess();

// returns the date in the local timezone
export function formatDateWithLocalTimezone(
  date: string | number,
  format = 'MM/DD/YY',
) {
  const parsedDate = dayjs.utc(date).tz(guessedTimezone);
  if (!parsedDate.isValid()) {
    return null;
  }
  return parsedDate.format(format);
}

// A fixed UTC offset as `+HH:MM` / `-HH:MM`, e.g. `-04:00`
const UTC_OFFSET_PATTERN = /^[+-]\d{2}:?\d{2}$/;

/**
 * Formats a UTC timestamp in the timezone described by a fixed UTC offset.
 *
 * Falls back to the user's timezone when no usable offset is given, which is
 * what we want for sources that tell us the time but not where it happened.
 * dayjs quietly ignores an offset it cannot parse and leaves the time in UTC,
 * so anything that isn't a `+HH:MM` / `-HH:MM` offset takes the fallback too.
 *
 * @param date UTC timestamp; a value with no zone is read as UTC
 * @param offset Fixed UTC offset of the timezone to display in
 * @param format dayjs format string
 * @returns Formatted time, or null when the timestamp is unparseable
 */
export function formatTimeWithUtcOffset(
  date: string | number,
  offset?: string | null,
  format = 'HH:mm',
) {
  const parsedDate = dayjs.utc(date);

  if (!parsedDate.isValid()) {
    return null;
  }

  if (offset && UTC_OFFSET_PATTERN.test(offset)) {
    return parsedDate.utcOffset(offset).format(format);
  }

  return parsedDate.tz(guessedTimezone).format(format);
}

export function getLocalTimezoneAbbreviation() {
  const dateTimeFormat = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'short',
    timeZone: guessedTimezone,
  });

  const dateParts = dateTimeFormat.formatToParts(new Date());

  const timezone = dateParts.find(part => {
    return part.type === 'timeZoneName';
  });

  return timezone?.value || '';
}
